import ExcelJS from "exceljs";

// Exportaciones de SG en .xlsx (antes CSV): Excel respeta columnas y acentos, y
// los IMEI/teléfonos/SIM van como texto, así que no se redondean ni salen en
// notación científica. Misma librería que barrido.ts de v5 (exceljs).

export type XlsxCell = string | number | Date | null | undefined;

export interface XlsxSheet {
  name: string;
  headers: string[];
  rows: XlsxCell[][];
}

export const XLSX_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const DATE_FORMAT = "dd/mm/yyyy hh:mm:ss";
const MIN_WIDTH = 8;
const MAX_WIDTH = 60;

// Encabezado como el de los barridos que hoy se arman a mano sobre el export de
// Wialon (azul claro D9E1F2, negrita), para que todos los informes se lean igual.
export const HEADER_FILL = "FFD9E1F2";

// Etiquetas con color: el cuadre se hace en Excel y la etiqueta tiene que verse
// sin leer la celda. Sólo valores exactos y distintivos; «Activo» o «Sí» no se
// pintan porque aparecen en columnas que no son etiquetas.
const TONE = {
  green: { fill: "FFE2EFDA", font: "FF375623" },
  amber: { fill: "FFFFF2CC", font: "FF7F6000" },
  red: { fill: "FFFCE4E4", font: "FF9C0006" },
  gray: { fill: "FFEDEDED", font: "FF595959" },
  blue: { fill: "FFDDEBF7", font: "FF1F4E79" },
  purple: { fill: "FFEDE4F7", font: "FF5B2C83" },
};
export const TAG_TONES: Record<string, keyof typeof TONE> = {
  Facturable: "green",
  "No facturable": "amber",
  Inactivo: "gray",
  Inactive: "gray",
  "Baja temporal": "amber",
  "Baja total": "red",
  Suspendido: "red",
  Almacen: "gray",
  Mantenimiento: "blue",
  "Revisión": "blue",
  "En proceso": "blue",
  Notificado: "blue",
  Demo: "purple",
  Pruebas: "purple",
};

// exceljs guarda la fecha en UTC; se corre al huso del navegador para que Excel
// muestre la hora local (mismo truco que excelLocalDate de barrido.ts).
const localDate = (d: Date) =>
  new Date(d.getTime() - d.getTimezoneOffset() * 60_000);

const cellText = (v: XlsxCell) =>
  v instanceof Date ? DATE_FORMAT : v == null ? "" : String(v);

export function styleTag(cell: ExcelJS.Cell) {
  const tone = typeof cell.value === "string" ? TAG_TONES[cell.value] : undefined;
  if (!tone) return;
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: TONE[tone].fill } };
  cell.font = { color: { argb: TONE[tone].font } };
}

export function addSgSheet(wb: ExcelJS.Workbook, { name, headers, rows }: XlsxSheet): ExcelJS.Worksheet {
  const ws = wb.addWorksheet(name, {
    views: [{ state: "frozen", ySplit: 1 }],
  });
  const head = ws.addRow(headers);
  head.font = { bold: true };
  head.alignment = { vertical: "middle", wrapText: true };
  head.eachCell((c) => {
    c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEADER_FILL } };
  });
  for (const r of rows) {
    // Strings se escriben tal cual (celda de texto): un IMEI nunca pasa a número.
    const row = ws.addRow(
      r.map((v) => (v instanceof Date ? localDate(v) : v ?? ""))
    );
    r.forEach((v, i) => {
      if (v instanceof Date) row.getCell(i + 1).numFmt = DATE_FORMAT;
      else if (typeof v === "string") styleTag(row.getCell(i + 1));
    });
  }
  headers.forEach((h, i) => {
    const longest = Math.max(
      h.length,
      ...rows.map((r) => cellText(r[i]).length)
    );
    ws.getColumn(i + 1).width = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, longest + 2));
  });
  ws.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: headers.length },
  };
  return ws;
}

export function buildSgWorkbook(
  sheetName: string,
  headers: string[],
  rows: XlsxCell[][]
): ExcelJS.Workbook {
  const wb = new ExcelJS.Workbook();
  addSgSheet(wb, { name: sheetName, headers, rows });
  return wb;
}

export function downloadBytes(filename: string, data: BlobPart, mime: string) {
  const url = URL.createObjectURL(new Blob([data], { type: mime }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function downloadWorkbook(filename: string, wb: ExcelJS.Workbook): Promise<void> {
  downloadBytes(filename, await wb.xlsx.writeBuffer(), XLSX_MIME);
}

// Un libro con varias hojas (p. ej. Descuadres: una por pregunta del cuadre).
export async function downloadXlsxSheets(filename: string, sheets: XlsxSheet[]): Promise<void> {
  const wb = new ExcelJS.Workbook();
  for (const s of sheets) addSgSheet(wb, s);
  await downloadWorkbook(filename, wb);
}

export async function downloadXlsx(
  filename: string,
  sheetName: string,
  headers: string[],
  rows: XlsxCell[][]
): Promise<void> {
  await downloadWorkbook(filename, buildSgWorkbook(sheetName, headers, rows));
}
